/**
 * Colours for the week grid's time blocks and the task-colour legend. Pure — no
 * React, no DOM — so every colour a task code can produce can be enumerated and
 * checked against WCAG in a unit test.
 *
 * A task code's colour is a hue/saturation/lightness derived from a hash of its
 * id. That hash colour is the block's left STRIPE (and the legend swatch). The
 * block FILL is a light tint of the same hue and the text sits on that tint, so
 * text contrast does not depend on the hue: it used to, and 46% of the
 * possible colours failed 4.5:1 on white or dark text chosen by HSL lightness,
 * which is not perceived brightness.
 */

export type Rgb = readonly [number, number, number];

/** Near-black text, as the grid has always used for "dark". */
export const DARK_TEXT = '#1a1a1a';
const DARK_TEXT_RGB: Rgb = [0x1a, 0x1a, 0x1a];

/** Secondary lines (name, times, hours) get a colour, not an opacity fade. */
export const DARK_TEXT_SECONDARY = '#404040';
const LIGHT_TEXT = '#ffffff';

/** Lightness of the block fill: a pale tint of the stripe's hue. */
export const BLOCK_FILL_LIGHTNESS = 92;

export interface Hsl {
  hue: number;
  saturation: number;
  lightness: number;
}

/**
 * The hash → HSL rule. Split out from {@link stringToHsl} so a test can walk
 * every hash residue instead of hoping a sample of strings hits them all.
 * `hash` is signed, and JS keeps the dividend's sign in `%`, so saturation
 * spans 46–84 and lightness 31–59 (not the 65–85 / 45–60 the old comment said).
 */
export function hslFromHash(hash: number): Hsl {
  return {
    hue: Math.abs(hash % 360),
    saturation: 65 + (hash % 20),
    lightness: 45 + (hash % 15),
  };
}

export function stringToHsl(str: string): Hsl {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return hslFromHash(hash);
}

/** The task code's own colour, as CSS: the block stripe and the legend swatch. */
export function stringToColor(str: string): string {
  const { hue, saturation, lightness } = stringToHsl(str);
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

/** HSL (degrees, percent, percent) to 8-bit sRGB, rounded as a browser paints it. */
export function hslToRgb(hue: number, saturation: number, lightness: number): Rgb {
  const s = saturation / 100;
  const l = lightness / 100;
  const chroma = (1 - Math.abs(2 * l - 1)) * s;
  const h = (((hue % 360) + 360) % 360) / 60;
  const x = chroma * (1 - Math.abs((h % 2) - 1));
  const [r1, g1, b1] =
    h < 1
      ? [chroma, x, 0]
      : h < 2
        ? [x, chroma, 0]
        : h < 3
          ? [0, chroma, x]
          : h < 4
            ? [0, x, chroma]
            : h < 5
              ? [x, 0, chroma]
              : [chroma, 0, x];
  const m = l - chroma / 2;
  const channel = (v: number) => Math.round((v + m) * 255);
  return [channel(r1), channel(g1), channel(b1)];
}

/** WCAG 2.x relative luminance of an sRGB colour. */
export function relativeLuminance([r, g, b]: Rgb): number {
  const linear = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}

/** WCAG 2.x contrast ratio between two sRGB colours, 1 to 21. */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Whichever of near-black and white has the higher contrast against `background`. */
export function getContrastColor(background: Rgb): string {
  const white: Rgb = [255, 255, 255];
  return contrastRatio(DARK_TEXT_RGB, background) >= contrastRatio(white, background)
    ? DARK_TEXT
    : LIGHT_TEXT;
}

export interface BlockPalette {
  /** The task code's own colour: the thick left stripe. */
  stripe: string;
  /** Light tint of the stripe's hue. */
  fill: string;
  /** The fill as sRGB, so a test can measure what the browser paints. */
  fillRgb: Rgb;
  /** Task code, and the colour every text line is measured against. */
  text: string;
  /** Name, times and hours lines. */
  secondaryText: string;
}

export function blockPalette(taskCodeId: string): BlockPalette {
  const { hue, saturation } = stringToHsl(taskCodeId);
  const fillRgb = hslToRgb(hue, saturation, BLOCK_FILL_LIGHTNESS);
  const text = getContrastColor(fillRgb);
  return {
    stripe: stringToColor(taskCodeId),
    fill: `hsl(${hue}, ${saturation}%, ${BLOCK_FILL_LIGHTNESS}%)`,
    fillRgb,
    text,
    secondaryText: text === DARK_TEXT ? DARK_TEXT_SECONDARY : LIGHT_TEXT,
  };
}

/** `#rrggbb` to sRGB, for measuring the text colours above. */
export function hexToRgb(hex: string): Rgb {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}
